import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import projects from './data/projects';
import customBedwarsProject from './data/customBedwarsProject';
import mylesEventProject from './data/mylesEventProject';
import pluginCommissionProject from './data/pluginCommissionProject';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import PortfolioPage from './pages/PortfolioPage';
import ProjectPageRouter from './pages/ProjectPageRouter';
import NavigationWrapper from './components/NavigationWrapper';
import CyclingImages from './components/project/CyclingImages';
import ScrollingWeapons from './components/project/ScrollingWeapons';
import SectionHeader from './components/SectionHeader';
import { renderParagraphs } from './utils/textFormatters';
import { ThemeProvider } from './context/ThemeContext';
import useDocumentTitle from './hooks/useDocumentTitle';

// Project detail route component
const ProjectDetail = () => {
  const { projectId } = useParams();
  const [showTrivia, setShowTrivia] = useState(false);
  
  // Combine all projects
  const allProjects = {
    ...projects,
    customBedwars: customBedwarsProject,
    mylesEvent: mylesEventProject,
    pluginCommission: pluginCommissionProject
  };
  
  // Call the hook before any conditional returns
  useDocumentTitle(allProjects[projectId]?.title || 'Not Found');
  
  if (!allProjects[projectId]) {
    return <Navigate to="/portfolio" />;
  }
  
  return (
    <Layout 
      activeSection="portfolio"
      showTrivia={showTrivia}
      setShowTrivia={setShowTrivia}
    >
      <ProjectPageRouter 
        projectId={projectId}
        project={allProjects[projectId]} 
        showTrivia={showTrivia} 
      />
    </Layout>
  );
};

const Portfolio = () => {
  const [enlargedImage, setEnlargedImage] = useState(null);

  const renderProject = (projectKey) => {
    const project = projects[projectKey];
    return (
      <div className="project-page">
        <div 
          className="h-[32rem] w-full bg-cover bg-center -mt-24 relative"
          style={{ backgroundImage: `url(${project.banner})` }}
        >
          {/* Add a semi-transparent overlay with blur */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
          
          <div className="text-center relative pt-56 z-10">
            <h1 className="text-6xl font-bold text-white mb-10 drop-shadow-lg text-shadow-lg">
              {project.title}
            </h1>
            <p className="text-xl text-white font-semibold mb-1 drop-shadow-md text-shadow-md">
              {project.role}
            </p>
            {project.startDate && (
              <p className="text-lg text-white/80 drop-shadow-md text-shadow-sm">
                {project.startDate} - {project.endDate || "Present"}
              </p>
            )}
          </div>
        </div>
        
        <div className="max-w-5xl mx-auto p-8">
          {/* Overview Section */}
          <SectionHeader title="Overview" />
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-4 text-[#294C6F]">What is Hoplite?</h3>
            <div className="text-lg text-[#333333] leading-relaxed">
              {/* First three paragraphs */}
              {renderParagraphs(project.overview.slice(0, 3))}
            </div>
            
            <h3 className="text-2xl font-bold mb-4 mt-8 text-[#294C6F]">What I Contributed:</h3>
            <div className="text-lg text-[#333333] leading-relaxed">
              {/* Remaining paragraphs */}
              {renderParagraphs(project.overview.slice(3))}
            </div>
          </div>

          {/* Legendary Weapons Section */}
          <div className="mb-16 mt-8">
            <SectionHeader title="Legendary Weapons" />
            {project.legendaryWeapons.pretext && (
              <p className="mb-4 text-[#333333]">{project.legendaryWeapons.pretext}</p>
            )}
            
            <ScrollingWeapons 
              weapons={project.legendaryWeapons.weapons} 
              contributionOrder={project.legendaryWeapons.contributionOrder} 
            />
            <div className="mb-8 text-lg text-[#333333] leading-relaxed">
              {renderParagraphs(project.legendaryWeapons.description)}
            </div>

            {/* YouTube Link for Legendary Weapons */}
            <div className="mb-6 text-center">
              <a 
                href="https://www.youtube.com/watch?v=k2eUWFo5f2o" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-[#2978C8] hover:text-[#294C6F] transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.615 3.184c-1.25-.5-2.5-.5-3.75-.5H8.135c-1.25 0-2.5 0-3.75.5C1.5 3.684 0 5.184 0 7.5v9c0 2.316 1.5 3.816 4.385 4.316 1.25.5 2.5.5 3.75.5h7.73c1.25 0 2.5 0 3.75-.5C22.5 20.316 24 18.816 24 16.5v-9c0-2.316-1.5-3.816-4.385-4.316zM9.545 15.537V8.463l6.001 3.037-6.001 3.037z"/>
                </svg>
                Watch Hoplite Particle Effect & Sound Design showcase on YouTube
              </a>
            </div>
          </div>

          {/* Champions Section */}
          <div className="mb-16">
            <SectionHeader title="Hoplite Champions Season 1" />
            
            {/* First line of description above the video */}
            <p className="mb-4 text-lg text-[#333333]">
              {project.champions.season1.description[0]}
            </p>
            
            <div className="aspect-video w-full mb-8">
              <iframe
                className="w-full h-full rounded-xl"
                src={project.champions.season1.videoUrl}
                title="Hoplite Champions Season 1"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            
            {/* Rest of the description (skipping any blank lines) */}
            <div className="text-lg text-[#333333] leading-relaxed">
              {renderParagraphs(project.champions.season1.description.slice(1).filter(line => line.trim() !== "" && line.trim() !== " "))}
            </div>
          </div>

          {/* World Generation Section */}
          <div className="mb-16">
            <SectionHeader title="World Generation" />
            
            {/* First line of description above the images */}
            <p className="mb-4 text-lg text-[#333333]">
              {project.worldGeneration.description[0]}
            </p>
            
            {/* Cycling Images for World Generation */}
            <div className="mb-2">
              <CyclingImages images={project.worldGeneration.progressImages} />
            </div>
            
            {/* GitHub Repository Link */}
            <div className="mb-6 text-center">
              <a 
                href="https://github.com/Invictable/TerraPackBuilderDemo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-[#2978C8] hover:text-[#294C6F] transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.302 3.438 9.8 8.205 11.387.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.09-.745.083-.73.083-.73 1.205.085 1.838 1.236 1.239 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z"/>
                </svg>
                View TerraPackBuilderDemo on GitHub
              </a>
            </div>
            
            {/* Rest of the description (skipping the blank line) */}
            <div className="text-lg text-[#333333] leading-relaxed">
              {renderParagraphs(project.worldGeneration.description.slice(2))}
            </div>
          </div>

          {/* Level Design Section */}
          <div className="mb-16">
            <SectionHeader title="Level Design" />
            <p className="mb-4 text-[#333333]">{project.levelDesignPreText}</p>
            
            {/* Citadel Structure - Image on left, text on right */}
            <div className="flex flex-col md:flex-row mb-12 gap-8">
              <div className="w-full md:w-1/2">
                <div className="flex flex-col gap-4">
                  {project.citadel.images.map((image, index) => (
                    <div 
                      key={index} 
                      className={`cursor-pointer transition-all duration-300 ${
                        enlargedImage === image ? 'fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' : ''
                      }`}
                      onClick={() => handleImageClick(image)}
                    >
                      <img
                        src={image}
                        alt={`Citadel ${index + 1}`}
                        className={`rounded-lg ${
                          enlargedImage === image ? 'max-h-[90vh] max-w-[90vw] object-contain' : 'w-full h-auto'
                        }`}
                      />
                    </div>
                  ))}
                </div>
                {project.citadel.trivia && (
                  <p className="mt-4 text-sm italic text-[#294C6F]">{project.citadel.trivia}</p>
                )}
              </div>
              <div className="w-full md:w-1/2 flex flex-col justify-start">
                <h3 className="text-2xl font-bold mb-4 text-[#294C6F] mt-0">Citadel Structure</h3>
                <div className="text-lg text-[#333333] leading-relaxed">
                  {renderParagraphs(project.citadel.description)}
                </div>
              </div>
            </div>
            
            {/* Deathmatch Arena - Text on left, image on right */}
            <div className="flex flex-col md:flex-row-reverse mb-12 gap-8">
              <div className="w-full md:w-1/2">
                <div className="flex flex-col gap-4">
                  {project.deathmatch.images.map((image, index) => (
                    <div 
                      key={index} 
                      className={`cursor-pointer transition-all duration-300 ${
                        enlargedImage === image ? 'fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' : ''
                      }`}
                      onClick={() => handleImageClick(image)}
                    >
                      <img
                        src={image}
                        alt={`Deathmatch Arena ${index + 1}`}
                        className={`rounded-lg ${
                          enlargedImage === image ? 'max-h-[90vh] max-w-[90vw] object-contain' : 'w-full h-auto'
                        }`}
                      />
                    </div>
                  ))}
                </div>

                {/* Hardcoded note that always displays */}
                <p className="mt-4 text-sm italic text-[#294C6F]">*this map has since been changed and no longer features an open center or increased chest spawns</p>
              </div>
              <div className="w-full md:w-1/2 flex flex-col justify-start">
                <h3 className="text-2xl font-bold mb-4 text-[#294C6F] mt-0">Acropolis Deathmatch Arena</h3>
                <div className="text-lg text-[#333333] leading-relaxed">
                  {renderParagraphs(project.deathmatch.description)}
                </div>
              </div>
            </div>
          </div>

          {/* Extra Section - As its own separate section */}
          <div className="mb-16">
            <SectionHeader title="Additional Contributions" />
            <div className="mb-8">
              <div className="flex flex-col gap-4 mb-6">
                {project.extra.images.map((image, index) => (
                  <div 
                    key={index} 
                    className={`cursor-pointer transition-all duration-300 ${
                      enlargedImage === image ? 'fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' : ''
                    }`}
                    onClick={() => handleImageClick(image)}
                  >
                    <img
                      src={image}
                      alt={`Additional Contribution ${index + 1}`}
                      className={`rounded-lg ${
                        enlargedImage === image ? 'max-h-[90vh] max-w-[90vw] object-contain' : 'w-full h-auto'
                      }`}
                    />
                  </div>
                ))}
              </div>
              <div className="text-lg text-[#333333] leading-relaxed">
                {renderParagraphs(project.extra.description)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleImageClick = (imageSrc) => {
    if (enlargedImage === imageSrc) {
      setEnlargedImage(null); // Shrink if clicked again
    } else {
      setEnlargedImage(imageSrc); // Enlarge if clicked first time
    }
  };

  return (
    <ThemeProvider>
      <Router>
        <Routes>
          {/* Home route */}
          <Route path="/" element={
            <NavigationWrapper>
              {(showTrivia, setShowTrivia) => (
                <Layout 
                  activeSection="home"
                  showTrivia={showTrivia}
                  setShowTrivia={setShowTrivia}
                >
                  <HomePage />
                </Layout>
              )}
            </NavigationWrapper>
          } />
          
          {/* Portfolio route */}
          <Route path="/portfolio" element={
            <NavigationWrapper>
              {(showTrivia, setShowTrivia) => (
                <Layout 
                  activeSection="portfolio"
                  showTrivia={showTrivia}
                  setShowTrivia={setShowTrivia}
                >
                  <PortfolioPage projects={projects} />
                </Layout>
              )}
            </NavigationWrapper>
          } />
          
          {/* Project detail route */}
          <Route path="/portfolio/:projectId" element={<ProjectDetail />} />
          
          <Route path="/resume" element={<Navigate to="/" replace />} />
          
          {/* Redirect any unknown routes to home */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default Portfolio;

